import { useState, useEffect } from "react";
import { Mail, Phone, MapPin, Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "@/lib/motion";
import { CONTACT, WEBSITE_TYPES, BUDGET_RANGES } from "@/lib/site-data";
import { onPrefill } from "@/lib/prefill";

const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;

const EMPTY = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  website_type: "",
  budget: "",
  project_details: "",
};

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Subscribe to prefill events (e.g. from portfolio card clicks)
  useEffect(() => {
    const unsub = onPrefill((patch) => {
      setDone(false);
      setForm((f) => ({ ...f, ...patch }));
    });
    return unsub;
  }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your full name.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email address.";
    if (!form.website_type) return "Please select the type of website you need.";
    if (!form.project_details.trim()) return "Please tell us a little about your project.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);

    const payload = {
      full_name: form.full_name.trim(),
      business_name: form.business_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      website_type: form.website_type,
      budget: form.budget,
      project_details: form.project_details.trim(),
    };

    // --- Primary: Formspree ---
    const sendViaFormspree = async () => {
      if (!FORMSPREE_ENDPOINT) {
        throw new Error("Formspree endpoint is not configured (REACT_APP_FORMSPREE_ENDPOINT missing).");
      }
      const subject = `New Yas Solutions inquiry: ${
        payload.business_name || payload.full_name
      } — ${payload.website_type}`;
      let response;
      try {
        response = await fetch(FORMSPREE_ENDPOINT, {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ ...payload, _subject: subject }),
        });
      } catch {
        throw new Error("Network error while contacting Formspree.");
      }
      let body = null;
      try { body = await response.json(); } catch { /* non-JSON */ }
      if (!response.ok) {
        let msg;
        if (Array.isArray(body?.errors) && body.errors.length) {
          msg = body.errors
            .map((er) => `${er.field ? er.field + ": " : ""}${er.message || er.code || "Invalid value"}`)
            .join(" ");
        } else if (response.status === 429) {
          msg = "Too many submissions right now — please wait a moment and try again.";
        } else {
          msg = body?.message || body?.error || `Formspree failed (${response.status}).`;
        }
        const err = new Error(msg);
        err.status = response.status;
        throw err;
      }
      return body;
    };

    // --- Safety-net fallback: backend /api/contact (shows up in /admin) ---
    const sendViaBackend = async () => {
      const BACKEND_API = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;
      const response = await fetch(BACKEND_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Backend fallback failed (${response.status}).`);
      }
      return await response.json();
    };

    try {
      try {
        await sendViaFormspree();
      } catch (fe) {
        // Log loudly so misconfiguration is diagnosable in prod devtools
        // eslint-disable-next-line no-console
        console.warn("[Yas Solutions] Formspree submission failed, falling back to admin backend:", fe);
        await sendViaBackend();
      }
      setDone(true);
      setForm(EMPTY);
      toast.success("Thanks! Your request has been sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32"
    >
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-[0.85fr_1.15fr]">
        {/* Left: info */}
        <Reveal>
          <span className="eyebrow">Get in touch</span>
          <h2 className="mt-5 font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl">
            Let's build something great together
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
            Request a <span className="text-white">free consultation</span> — no
            pressure, no obligation. Tell us about your business and we'll get back to
            you with honest advice and a clear quote.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email-link"
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 transition-colors hover:border-neon-cyan/40"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 text-neon-cyan">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-white/40">Email</p>
                <p className="text-sm font-medium text-white">{CONTACT.email}</p>
              </div>
            </a>
            <div
              data-testid="contact-phone-link"
              className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 text-neon-cyan">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-white/40">Phone</p>
                <p className="text-sm font-medium text-white">{CONTACT.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 text-neon-cyan">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-white/40">Where</p>
                <p className="text-sm font-medium text-white">{CONTACT.location}</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Right: form */}
        <Reveal delay={0.1}>
          <div className="surface-card rounded-3xl p-6 md:p-9">
            {done ? (
              <div
                data-testid="contact-success"
                className="flex min-h-[420px] flex-col items-center justify-center text-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-neon-cyan to-neon-purple text-black">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-semibold text-white">
                  Request received
                </h3>
                <p className="mt-3 max-w-sm text-sm text-white/60">
                  Thank you for reaching out to Yas Solutions. We'll review your
                  project and reply within one business day.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="contact-reset-button"
                  className="mt-7 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-white/70">Full name *</Label>
                    <Input
                      id="full_name"
                      data-testid="input-full-name"
                      value={form.full_name}
                      onChange={set("full_name")}
                      placeholder="Jane Doe"
                      className="h-12 rounded-xl border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus-visible:ring-neon-cyan/60"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_name" className="text-white/70">Business name</Label>
                    <Input
                      id="business_name"
                      data-testid="input-business-name"
                      value={form.business_name}
                      onChange={set("business_name")}
                      placeholder="Acme Co."
                      className="h-12 rounded-xl border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus-visible:ring-neon-cyan/60"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white/70">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="input-email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="jane@acme.com"
                      className="h-12 rounded-xl border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus-visible:ring-neon-cyan/60"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white/70">Phone number</Label>
                    <Input
                      id="phone"
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="(555) 000-0000"
                      className="h-12 rounded-xl border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus-visible:ring-neon-cyan/60"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-white/70">Type of website *</Label>
                    <Select value={form.website_type} onValueChange={set("website_type")}>
                      <SelectTrigger
                        data-testid="select-website-type"
                        className="h-12 rounded-xl border-white/10 bg-white/[0.03] text-white focus:ring-neon-cyan/60"
                      >
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        {WEBSITE_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`website-type-${t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Estimated budget</Label>
                    <Select value={form.budget} onValueChange={set("budget")}>
                      <SelectTrigger
                        data-testid="select-budget"
                        className="h-12 rounded-xl border-white/10 bg-white/[0.03] text-white focus:ring-neon-cyan/60"
                      >
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_RANGES.map((b) => (
                          <SelectItem key={b} value={b} data-testid={`budget-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project_details" className="text-white/70">Project details *</Label>
                  <Textarea
                    id="project_details"
                    data-testid="input-project-details"
                    value={form.project_details}
                    onChange={set("project_details")}
                    placeholder="Tell us about your business, goals and anything you'd like your new website to do…"
                    rows={5}
                    className="rounded-xl border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus-visible:ring-neon-cyan/60"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send My Request
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-white/40">
                  We typically reply within one business day. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
